import { useState } from "react";
import RichTextEditor from "react-rte";
import { useNavigate } from "react-router-dom";
import { TextField, FormHelperText } from "@mui/material";
import imageUpload from "../../../services/upload";
import knowledgeService from "../../../services/knowledgeService";
import { IMAGE_PATH, instanceToken } from "../../../utils/constant";
import { createNewKnowledgeSchema } from "../../../schema/createNewKnowledgeSchema";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  EditPageLayout,
  EditImageSection,
  EditFieldLabel,
  EditFormTopSection,
  EditFormParagraphsSection,
} from "../../../components/edit/EditPageLayout";

const imgFileTypes = ["image/gif", "image/jpeg", "image/png", "image/svg+xml"];

export default function CreateKnowledge({ homeAlert }) {
  const navigate = useNavigate();
  const [parEng, setParEng] = useState(RichTextEditor.createEmptyValue());
  const [parMM, setParMM] = useState(RichTextEditor.createEmptyValue());
  const [imagePreview, setImagePreview] = useState("");
  const [imageFile, setImageFile] = useState("");
  const [imageFileUrl, setImageFileUrl] = useState("");
  const [values, setValues] = useState({ image_url: "" });
  const [, setKnowledges] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState({});

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(createNewKnowledgeSchema),
    initialValues: {
      title_en: "",
      title_mm: "",
      paragraph_en: "<p></p>",
      paragraph_mm: "<p></p>",
    },
  });

  async function getImageUrl(imgData) {
    const response = await imageUpload.getImageUrl(
      instanceToken.token,
      imgData
    );
    const result = response.data;
    const path = IMAGE_PATH + result?.imageName;
    setImageFileUrl(result?.imageUploadUrl);
    setValues({
      ...values,
      image_url: path,
    });
  }

  const changeFile = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const img = e.target.files[0];

      if (!imgFileTypes.includes(img.type)) {
        setErrorMessage({
          ...errorMessage,
          image_url: "Please select image. (PNG, JPG, JPEG, GIF, ...)",
        });
        return;
      }
      if (img.size > 204800) {
        setErrorMessage({
          ...errorMessage,
          image_url: "Image file size must be smaller than 200KB.",
        });
        return;
      }
      setImageFile(img);
      setImagePreview(URL.createObjectURL(img));
      getImageUrl(img);
    }
  };

  const formData = new FormData();

  const handleCreate = async (data) => {
    formData.append("title_en", data?.title_en);
    formData.append("title_mm", data?.title_mm);
    formData.append("paragraph_en", data?.paragraph_en);
    formData.append("paragraph_mm", data?.paragraph_mm);
    formData.append("image_url", values?.image_url);
    formData.append("image_data", "");

    try {
      setLoading(true);
      await imageUpload.uploadImage(imageFileUrl, imageFile);
      const response = await knowledgeService.postKnowledge(
        instanceToken.token,
        formData
      );
      navigate("/knowledges");
      setKnowledges(response.data);
      homeAlert("News Knowledges have been created.", false);
    } catch (error) {
      console.log(error);
      homeAlert("Error on server.", true);
    } finally {
      setLoading(false);
      setErrorMessage({});
    }
  };

  return (
    <EditPageLayout
      title="Create Knowledge Post"
      subtitle="Add knowledge post details and content in both languages."
      breadcrumbs={[
        { label: "Knowledges", path: "/knowledges" },
        { label: "Create" },
      ]}
      listPath="/knowledges"
      loading={loading}
      submitLabel="Create"
      onSubmit={handleSubmit(handleCreate)}
    >
      <EditFormTopSection
        image={
          <EditImageSection
            uploadLabel="Knowledge Image"
            previewLabel="Image Preview"
            imagePreview={imagePreview}
            onFileChange={changeFile}
            error={errorMessage["image_url"]}
            recommendedSize="1920 x 1080 px (16:9)"
          />
        }
      >
        <div>
          <EditFieldLabel required>Title (English)</EditFieldLabel>
          <TextField
            fullWidth
            id="title_en"
            {...register("title_en")}
            error={!!errors.title_en}
            helperText={errors.title_en?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Title (Myanmar)</EditFieldLabel>
          <TextField
            fullWidth
            id="title_mm"
            {...register("title_mm")}
            error={!!errors.title_mm}
            helperText={errors.title_mm?.message}
          />
        </div>
      </EditFormTopSection>

      <EditFormParagraphsSection>
        <div className="edit-editor-field">
          <EditFieldLabel required>Paragraph (English)</EditFieldLabel>
          <Controller
            name="paragraph_en"
            control={control}
            render={() => (
              <>
                <RichTextEditor
                  value={parEng}
                  onChange={(value) => {
                    setParEng(value);
                    setValue("paragraph_en", value.toString("html"));
                  }}
                />
                {errors.paragraph_en && (
                  <FormHelperText error>
                    {errors.paragraph_en.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
        </div>

        <div className="edit-editor-field">
          <EditFieldLabel required>Paragraph (Myanmar)</EditFieldLabel>
          <Controller
            name="paragraph_mm"
            control={control}
            render={() => (
              <>
                <RichTextEditor
                  value={parMM}
                  onChange={(value) => {
                    setParMM(value);
                    setValue("paragraph_mm", value.toString("html"));
                  }}
                />
                {errors.paragraph_mm && (
                  <FormHelperText error>
                    {errors.paragraph_mm.message}
                  </FormHelperText>
                )}
              </>
            )}
          />
        </div>
      </EditFormParagraphsSection>
    </EditPageLayout>
  );
}
